import React from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import first from 'lodash/first';
import { useTranslation } from 'react-i18next';

import { BaseReferenceDto } from '@cfa/pathway-fe-shared/xp';
import {
  SearchData,
  SearchDocument as Document,
} from '@cfa/pathway-fe-shared/xp';
import { Card, Typography } from '@cfa/react-components';

import { UnkeyedDocument } from '@/hooks/useGetStructuredBrowseData';

import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants';

import { ProcedureStep } from '../AddProcedurePopup';
import { PROCEDURE_STEPS } from '../constants';
import { PopupDocument } from '../types';
import { useAddProcedureSearchResults } from '../useAddProcedureSearchResults';

import DocumentCheckbox from './DocumentCheckbox';

import { userPrefsAtom } from '@/atoms/userPrefs';
import SubcategoryDocument from '@/routes/Explore/Operations/components/SubcategoryDocument';

interface SearchResultsProps {
  searchTermQuery: string;
  setPreviewDocument: (
    doc:
      | Document
      | BaseReferenceDto
      | UnkeyedDocument
      | Record<string, unknown>,
  ) => void;
  setActiveStep: (step: ProcedureStep) => void;
  onSearchTermSelect: (
    suggestion: string | null,
    spellingSuggestion?: string,
  ) => void;
  selectedDocuments: PopupDocument[];
  setSelectedDocuments: (documents: PopupDocument[]) => void;
  selectedCountryCode?: string;
  onlyAllowOneDoc?: boolean;
}

export const StepThreeSearchResults: React.FC<SearchResultsProps> = ({
  selectedCountryCode,
  searchTermQuery,
  setPreviewDocument,
  setActiveStep,
  onSearchTermSelect,
  selectedDocuments,
  setSelectedDocuments,
  onlyAllowOneDoc = false,
}) => {
  const onDocumentClick = (doc: Document) => {
    setPreviewDocument(doc);
    setActiveStep(PROCEDURE_STEPS.PREVIEW);
  };

  const { t } = useTranslation();

  const { language, country } = useAtomValue(userPrefsAtom);

  const searchParams: SearchData['query'] = {
    country: selectedCountryCode ?? country.id ?? '',
    language,
    query: searchTermQuery,
  };

  const {
    data: searchData,
    isFetching,
    isPending,
    error,
  } = useAddProcedureSearchResults(searchParams, searchTermQuery);

  const searchResponse = searchData?.summary;
  const visibleResults = searchData?.visibleResults ?? [];
  const visibleResultCount = visibleResults.length;
  const misspellingSuggestion =
    first(searchResponse?.spellingSuggestions) ?? '';

  if (error) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  return (
    <>
      <LoadingOverlay isOpen={isFetching || isPending} />
      {misspellingSuggestion && !isFetching && (
        <span
          data-testid="MisspellingSuggestionWrapper"
          className="text-grayscale-gray7 mb-4 font-normal"
        >
          {t('Search.numberOfResultsFor')}
          {': '}
          <button
            type="button"
            className="text-brand-navy! dark:text-contrast! font-bold!"
            data-testid="MisspellingSuggestion"
            onClick={() => onSearchTermSelect(null, misspellingSuggestion)}
          >
            {misspellingSuggestion}
          </button>
        </span>
      )}
      {!!searchResponse && (
        <Typography className="text-left!" variant="body1">
          {` ${visibleResultCount} ${t('Search.numberOfResultsFor')} `}
          <strong>{searchTermQuery}</strong>
        </Typography>
      )}
      <div className="list-results">
        {!visibleResults.length ? (
          <div className="px-8 py-6">{t('Search.noResults')}</div>
        ) : (
          visibleResults
            .filter((document): document is Document & { id: string } => {
              if (!document.id) {
                datadogRum.addError(
                  new Error('Document missing ID in search results', {
                    cause: {
                      document,
                      searchTermQuery,
                      selectedCountryCode,
                      language,
                    },
                  }),
                );
                return false;
              }
              return true;
            })
            .map(document => {
              const newId = document.id;

              const newDocument = {
                id: newId,
                type: Constants.STEP_TYPES.DOCUMENT,
                reference: {
                  id: newId,
                },
                name: {
                  en: language === 'en' ? document.name : null,
                  es: language === 'es' ? document.name : null,
                },
              } as PopupDocument;
              return (
                <Card
                  className="animate-fade-in! my-4! flex! flex-row! items-center! justify-between! pr-4!"
                  key={newId}
                  title={document.name}
                >
                  <SubcategoryDocument
                    icon={document.icon}
                    id={newId}
                    name={document.name ?? ''}
                    onClick={() => onDocumentClick(document)}
                  />
                  <DocumentCheckbox
                    documentId={newId}
                    newDocument={newDocument}
                    selectedDocuments={selectedDocuments}
                    setSelectedDocuments={setSelectedDocuments}
                    onlyAllowOneDoc={onlyAllowOneDoc}
                  />
                </Card>
              );
            })
        )}
      </div>
    </>
  );
};

export default StepThreeSearchResults;
